import { deriveErrorMessage } from '@/lib/utils'
import type { MouseEvent, ReactNode } from 'react'
import { toast as sonnerToast } from 'sonner'

type ToastOptions = {
  title?: ReactNode
  description?: ReactNode
  variant?: 'default' | 'destructive' | 'success'
  error?: unknown
  duration?: number
  /**
   * Reuse an id a previous `toast()` returned to REPLACE that toast in place —
   * same slot, no second toast. One action that takes time then reports a
   * result (a room move: pending Undo, then armed Undo, then "Move undone") is
   * one piece of news, so it stays one toast.
   */
  id?: string | number
  /**
   * Single inline button — sonner renders it; used for "Undo" on a room move.
   *
   * `onClick` receives the event: sonner closes the toast after the handler
   * unless it is `preventDefault()`ed, and a handler that starts more work has
   * to keep the toast to report how that work ended. `className` lands on the
   * button ON TOP of the Toaster's own action-button styling (see `__root.tsx`),
   * for per-toast states like "not clickable yet".
   */
  action?: { label: ReactNode; onClick: (event: MouseEvent<HTMLButtonElement>) => void; className?: string }
}

// Compat shim over sonner keeping the legacy `toast({ title, description, variant, error })` shape.
function toast({ title, description, variant = 'default', error, duration, id, action }: ToastOptions) {
  if (error && !description) {
    description = deriveErrorMessage(error)
  }

  // Every key is spelled out, `undefined` included: sonner MERGES an update over
  // the toast it replaces, so a key left off keeps the previous value instead of
  // clearing it — which is how an armed Undo button would survive into the
  // "Move undone" confirmation that has nothing left to undo.
  const options = {
    id,
    description,
    duration,
    action: action && { label: action.label, onClick: action.onClick },
    classNames: { actionButton: action?.className },
  }

  if (variant === 'destructive') return sonnerToast.error(title, options)
  if (variant === 'success') return sonnerToast.success(title, options)
  return sonnerToast(title, options)
}

/** Dismiss one toast by the id `toast()` returned. */
function dismissToast(id: string | number) {
  sonnerToast.dismiss(id)
}

export { dismissToast, toast }
